import { IStyle } from "./../../../Types/Style";
import { GetStyleResponse, GetOrganizationResponse } from "./../../Organization/sagas";
import * as R from "ramda";
import { Action, ActionFunction1 } from "redux-actions";
import { call, put, select } from "redux-saga/effects";
import { _getOrganizationById, _getStyleById } from "../../Organization/sagas";
import { updateStyles as updateOrgStyles } from "../../../actions/organizations";
import { SagaFunction } from "../../../actions/types";
import { IFileUploadResponseData } from "../../../api/CourseAuthorApi";
import {
  FontStyle,
  FontWeight,
  IFont,
  IFullFontBody,
  IFullFontResponse,
  ISource,
  SourceKind,
} from "../../../api/FontsApi/types";
import { AxiosInterceptedResult } from "../../../api/interceptors";
import { IDisplayInfoResponse, IResolveResponse, ResolvableType } from "../../../api/ResolveApi/resolve-api.types";
import { IPatchPayload } from "../../../components/course-author/helpers/patches";
import { patchPayloadCreator } from "../../../components/course-author/helpers/patches/creators";
import { dependencies } from "../../../dependencies";
import { hasErrorRecords, ICreationResponse } from "../../../parsers/resolve";
import { SagaAction } from "../../../sagas/types";
import * as selectors from "../../../selectors/selectors";
import { ICompleteOrganization, IFontFamily } from "../../types/common";
import { modifyFontSection, modifyLogoPatch } from "../helpers/patches";
import {
  IAddNewLogoPayload,
  IApplyFontToSectionPayload,
  IFirstFetchPayload,
  IPatchStylePayload,
  loadOrgFontFamilies as loadOrgFontFamiliesAction,
  patchStyle as patchStyleAction,
  updateStyles as updateStylesAction,
} from "./";

const { api } = dependencies;

interface IFurtherActions {
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
  onSuccess: ActionFunction1<IFirstFetchPayload, Action<IFirstFetchPayload>>;
}

export const firstFetch = (fa: IFurtherActions) =>
  function* _firstFetch(action: Action<string>): SagaAction {
    const styleId = action.payload;
    const organizationId: string = yield select(selectors.organizationId);
    const accountId: string = yield select(selectors.accountId);
    const [err, style]: GetStyleResponse = yield call(_getStyleById, styleId, accountId);
    const [getOrgErr, organization]: GetOrganizationResponse = yield call(
      _getOrganizationById,
      organizationId,
      accountId
    );
    if (err || !style || getOrgErr || !organization) {
      yield put(fa.onFailure({ message: err || getOrgErr || "Unknown error" }));
      return;
    }

    yield put(fa.onSuccess({ organization, style }));
    yield put(loadOrgFontFamiliesAction());
  };

interface ILoadOrgFontFamiliesFurtherActions {
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
  onSuccess: ActionFunction1<IFontFamily[], Action<IFontFamily[]>>;
}

export const loadOrgFontFamilies = (fa: ILoadOrgFontFamiliesFurtherActions) =>
  function* _loadOrgFontFamilies(_: Action<void>): SagaAction {
    const accountId: string = yield select(selectors.accountId);
    const organizationId: string = yield select(selectors.organizationId);
    const [idsErr, idsResp]: AxiosInterceptedResult<string[]> = yield call(
      api.fonts.getEditableByRelativeTo,
      accountId,
      organizationId
    );

    if (idsErr || !idsResp) {
      yield put(fa.onFailure({ message: "Could not get font ids" }));
      return;
    }
    const fontIds: Array<[ResolvableType, string]> = idsResp.data.map(respId => [ResolvableType.FontFamily, respId]);
    const [fontsErr, fontsDisplayNamesResp]: AxiosInterceptedResult<Array<
      IResolveResponse<IDisplayInfoResponse>
    >> = yield call(api.resolve.displayInfo.post, accountId, fontIds, organizationId);
    if (fontsErr || !fontsDisplayNamesResp || hasErrorRecords(fontsDisplayNamesResp.data)) {
      yield put(fa.onFailure({ message: "Could not get display information for fonts" }));
      return;
    }
    const data: IFontFamily[] = R.zipWith(
      (ffmId, { value }) => ({
        displayName: value.displayName || "",
        ffmId,
      }),
      idsResp.data,
      fontsDisplayNamesResp.data
    );
    yield put(fa.onSuccess(data));
  };

interface IFAPatchStyle {
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
  onSuccess: ActionFunction1<IStyle, Action<IStyle>>;
}

export const patchStyle = (fa: IFAPatchStyle) =>
  function* _patchStyle(action: Action<IPatchStylePayload>): SagaAction {
    const styleId: string = yield select(selectors.currentStyleId);
    const accountId: string = yield select(selectors.accountId);
    const [err, resp]: AxiosInterceptedResult<IResolveResponse<{}>> = yield call(
      api.styles.patchStyle,
      accountId,
      styleId,
      action.payload.patch
    );
    if (err || !resp) {
      yield put(fa.onFailure({ message: err || "Error when patching a style" }));
      return;
    }
    const [getStyleErr, style]: GetStyleResponse = yield call(_getStyleById, styleId, accountId);
    if (getStyleErr || !style) {
      yield put(fa.onFailure({ message: getStyleErr || "Unknown error" }));
      return;
    }
    yield put(fa.onSuccess(style));
    yield put(updateStylesAction());
  };

interface IAddNewLogoFurtherActions {
  onSuccess: ActionFunction1<void, Action<void>>;
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
}

export const addNewLogo = (fa: IAddNewLogoFurtherActions): SagaFunction =>
  function* _addNewLogo(action: Action<IAddNewLogoPayload>): SagaAction {
    const { link, style } = action.payload;
    const files = action.payload.files || [];
    if (!files.length && !link) {
      yield put(
        fa.onFailure({
          message: "Image and Link are empty. Please, make sure you provide at least one.",
        })
      );
      return;
    }

    const activeUser: string = yield select(selectors.accountId);
    const [uploadError, uploadResponse]: AxiosInterceptedResult<IFileUploadResponseData[]> =
      files.length > 0
        ? yield call(api.media.uploadFile, files[0], activeUser)
        : yield call(api.media.uploadViaUrl, link || "", activeUser);

    if (uploadError || !uploadResponse) {
      yield put(
        fa.onFailure({
          message: `There was an error uploading the given file: ${uploadError || ""}`,
        })
      );
      return;
    }
    const { mediaId } = uploadResponse.data[0] || uploadResponse.data;
    const imgId = mediaId.slice(4);
    const patch = modifyLogoPatch(style, activeUser, imgId);
    yield put(
      patchStyleAction({
        location: "Logo",
        patch: patchPayloadCreator(style.versionId, patch),
      })
    );
    yield put(fa.onSuccess());
  };

const googleSource = (family: string): ISource => ({
  fileFont: {
    name: "",
  },
  googleFont: { family },
  kind: SourceKind.Google,
  literalFont: { family: "" },
});

const defaultFont = (family: string): IFont => ({
  source: googleSource(family),
  style: FontStyle.FSNormal,
  weight: FontWeight.FW400,
});

export const mkDefaultFont = (fontFamily: string, owner: string): IFullFontBody => ({
  boldFont: defaultFont(fontFamily),
  boldItalicFont: defaultFont(fontFamily),
  deleted: false,
  displayName: fontFamily,
  fallback: ["sans-serif"],
  italicFont: defaultFont(fontFamily),
  normalFont: defaultFont(fontFamily),
  owner,
});

interface IApplyFontToSectionFurtherActions {
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
  onSuccess: ActionFunction1<void, Action<void>>;
}

export const applyFontToSection = (fa: IApplyFontToSectionFurtherActions) =>
  function* _applyFontToSection(action: Action<IApplyFontToSectionPayload>): SagaAction {
    const { fontOption, section, style } = action.payload;
    const accountId: string = yield select(selectors.accountId);
    const onwer: string = yield select(selectors.organizationId);
    if (fontOption.ffmId) {
      const assignFontPatch = patchPayloadCreator(
        style.versionId,
        modifyFontSection(style, accountId, fontOption.ffmId, section)
      );
      yield put(patchStyleAction({ location: "Fonts", patch: assignFontPatch }));
      yield put(fa.onSuccess());
      return;
    }
    const font = mkDefaultFont(fontOption.name, onwer);
    const [creationErr, creationResp]: AxiosInterceptedResult<ICreationResponse<IFullFontResponse>> = yield call(
      api.fonts.create,
      font,
      accountId
    );
    if (creationErr || !creationResp) {
      yield put(fa.onFailure({ message: "Could not create font" }));
      return;
    }
    const ffmId = creationResp.data.value;
    const assignNewFontPatch = modifyFontSection(style, accountId, ffmId, section);
    yield put(
      patchStyleAction({
        location: "Fonts",
        patch: patchPayloadCreator(style.versionId, assignNewFontPatch),
      })
    );
    yield put(loadOrgFontFamiliesAction());
    yield put(fa.onSuccess());
  };

interface IFAPatchOrganization {
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
  onSuccess: ActionFunction1<ICompleteOrganization, Action<ICompleteOrganization>>;
  callback?: () => Action<void>;
}

export const patchOrganization = (fa: IFAPatchOrganization) =>
  function* _patchOrganization(action: Action<IPatchPayload>): SagaAction {
    const orgId: string = yield select(selectors.organizationId);
    const accountId: string = yield select(selectors.accountId);
    const [err, resp]: AxiosInterceptedResult<IResolveResponse<{}>> = yield call(
      api.organizations.patchOrganization,
      accountId,
      orgId,
      action.payload
    );
    if (err || !resp) {
      yield put(fa.onFailure({ message: "Could not patch organisation" }));
      return;
    }
    const [getOrgErr, org]: GetOrganizationResponse = yield call(_getOrganizationById, orgId, accountId);
    if (getOrgErr || !org) {
      yield put(fa.onFailure({ message: getOrgErr || "Unknown error" }));
      return;
    }
    yield put(fa.onSuccess(org));
    if (fa.callback) {
      yield put(fa.callback());
    }
  };

export const updateStyles = () =>
  function* _updateStyles(): SagaAction {
    const { window, saltcomplianceVersion, saltcomplianceApiRoot } = dependencies;
    const organizationId: string = yield select(selectors.organizationId);
    const timestamp: string = `${Date.now()}`;
    yield call(
      [window.localStorage, window.localStorage.setItem],
      dependencies.saltcomplianceLocalStorageKeys.fetchTimestamp,
      timestamp
    );
    yield call(updateOrgStyles, window, saltcomplianceApiRoot, organizationId, saltcomplianceVersion, timestamp);
  };
